import { BRAND } from "../brand";

/** 21ik Platform admin panel (/yonetim) and support-session texts on the company side (English). */
export const platform = {
  name: `${BRAND.name} Platform`,
  nav: {
    label: "Platform menu",
    overview: "Overview",
    tenants: "Companies",
    people: "Find a person",
    jobs: "Background jobs",
    audit: "Platform log",
    admins: "Admins",
    logout: "Sign out",
    skip: "Skip to content",
  },
  login: {
    title: "Sign in to the admin panel",
    lead: `For the ${BRAND.name} team only. Company accounts can't sign in here.`,
    email: "Email",
    password: "Password",
    submit: "Sign in",
    companyLink: "Signing in with a company account?",
    companyLinkCta: "Company sign-in",
    expired: "Your panel session expired. Please sign in again.",
    codeLead: "Two-step verification: enter the code from your authenticator app for {email}.",
    code: "Verification code",
    codeHint: "6-digit code, refreshed every 30 seconds.",
    codeSubmit: "Verify and sign in",
    codeBack: "Sign in with another account",
  },
  common: {
    loading: "Loading…",
    none: "—",
    retry: "Try again",
    back: "Back",
    cancel: "Cancel",
    close: "Close",
    more: "Show more",
    prev: "Previous",
    next: "Next",
    open: "Open",
    active: "Active",
    suspended: "Suspended",
    disabled: "Disabled",
    locked: "Locked",
    yes: "Yes",
    no: "No",
    minutesLeft_one: "{count} min left",
    minutesLeft_other: "{count} min left",
    ago: "{since} ago",
    agoMinutes: "{count} min ago",
    agoHours: "{count} h ago",
    agoDays_one: "{count} day ago",
    agoDays_other: "{count} days ago",
    never: "Never",
    page: "{from}–{to} of {total}",
  },
  roles: {
    admin: "System admin",
    hr_manager: "HR manager",
    hr_specialist: "HR specialist",
    finance: "Finance",
    executive: "Executive",
    employee: "Employee",
    noEmployee: "No employee record",
  },
  modules: {
    leave: "Leave requests",
    expenses: "Expense claims",
    advances: "Advance requests",
    documents: "Documents",
    payslips: "Payslips",
    announcements: "Announcements",
  },
  setup: {
    company: "Company details",
    schedule: "Work schedule",
    structure: "Branches and departments",
    employees: "Employees",
    invitations: "Invitations",
    progress: "{done}/{total}",
  },
  endReasons: {
    ended_by_admin: "Ended by support",
    ended_from_panel: "Ended from the panel",
    platform_logout: "Signed out of the panel",
    tenant_suspended: "Company suspended",
    revoked_by_tenant: "Company turned off support access",
    revoked: "Session closed",
    expired: "Expired",
    active: "In progress",
  },
  overview: {
    title: "Overview",
    lead: "Customers, usage and system health. All companies are counted, including sample ones.",
    updated: "Updated {time}",
    refresh: "Refresh",
    attention: "Needs attention",
    attentionEmpty: "Nothing needs attention right now.",
    attentionKinds: {
      setup_incomplete: "{name} · setup {done}/5 · signed up {date}",
      silent_tenant: "{name} · nobody has signed in for over 14 days",
      silent_tenant_never: "{name} · nobody has signed in yet",
      dead_jobs: "{count} jobs failed in the last 7 days",
      queue_delay: "{count} jobs waiting · oldest {since}",
      support_active: "Open support session · {name} · ends {time}",
      suspended: "{name} · suspended",
    },
    customers: "Customers",
    tenants: "Customer companies",
    tenantsValue: "{total} · {active} active · {suspended} suspended",
    newTenants: "New sign-ups",
    newTenantsValue: "Last 7 days {d7} · last 30 days {d30}",
    employees: "Employee records",
    employeesHint: "Excluding leavers",
    accounts: "User accounts",
    activeUsers: "Active users",
    activeUsersValue: "7 days {d7} · 30 days {d30}",
    activeUsersHint: "Accounts that signed in or used the app in the window; support sessions don't count.",
    usage: "Module usage (last 30 days)",
    usageModule: "Module",
    usage30: "Records (30 days)",
    usageTenants: "Companies using",
    usageTotal: "Total",
    recent: "Recently signed-up companies",
    jobs: "Background jobs",
    jobsPending: "Pending",
    jobsRunning: "Running",
    jobsDead: "Failed",
    jobsDeadValue: "{total} · last 7 days {d7}",
    jobsOldest: "Oldest pending",
    jobsLast: "Last finished",
    jobsLink: "Go to jobs",
    support: "Open support sessions",
    supportEmpty: "No support session is open.",
  },
  tenants: {
    title: "Companies",
    lead: "All customer companies and how they use the product.",
    search: "Search companies",
    searchPlaceholder: "Name or short name",
    status: "Status",
    statusAll: "All",
    sort: "Sort",
    sortCreated: "Newest",
    sortName: "Name",
    sortActivity: "Last activity",
    sortEmployees: "Employees",
    caption: "Companies",
    colTenant: "Company",
    colStatus: "Status",
    colCreated: "Signed up",
    colEmployees: "Employees",
    colAccounts: "Accounts",
    colActive7: "Active 7 days",
    colActivity: "Last activity",
    colSetup: "Setup",
    empty: "No company matches this search.",
    total_one: "{count} company",
    total_other: "{count} companies",
  },
  tenant: {
    back: "Companies",
    lead: "{slug} · signed up {date} · {timezone}",
    support: "View as company",
    suspend: "Suspend",
    reactivate: "Reopen",
    state: "Status",
    stateStatus: "Account status",
    stateSupport: `${BRAND.name} support access`,
    supportOn: "On",
    supportOff: "Off",
    supportChanged: "changed on {date}",
    lastChange: "Last status change",
    lastChangeValue: "{action} · {date} · {admin}",
    counts: "Numbers",
    employees: "Employee records",
    accounts: "Active accounts",
    pendingInvitations: "Pending invitations",
    activeUsers: "Active users",
    activeUsersValue: "7 days {d7} · 30 days {d30}",
    lastActivity: "Last activity",
    structure: "Branches and departments",
    structureValue: "{branches} branches · {departments} departments",
    pendingApprovals: "Pending approval steps",
    storage: "File storage",
    roleHolders: "Role holders",
    roleHoldersLead: "Customer contacts: name and role only. The employee list isn't shown in the panel.",
    colName: "Name",
    colRoles: "Roles",
    colLastLogin: "Last sign-in",
    viewAs: "View as {name}",
    noRoleHolders: "No active account with a role.",
    setup: "Setup progress",
    setupDone: "Done",
    setupOpen: "Missing",
    usage: "Module usage",
    usage30: "Last 30 days",
    usageTotal: "Total",
    quality: "Data quality",
    qualityLead: "Counts from the “Show gaps” rules only; no names.",
    sessions: "Support sessions",
    sessionsEmpty: "No support session has been opened for this company.",
    colWhen: "Started",
    colAdmin: `${BRAND.name} staff`,
    colTarget: "Viewed as",
    colReason: "Reason",
    colDuration: "Duration",
    colEnd: "End",
    durationValue: "{count} min",
  },
  supportDialog: {
    title: "View as company",
    lead: "You'll see the screens of a person at {company}, read-only.",
    person: "View as",
    personHint: "Role holders are listed; type a name to find an employee (at least 2 letters).",
    personSearch: "Search people",
    personEmpty: "No active account matches this search.",
    personRequired: "Choose a person.",
    reason: "Reason",
    reasonHint: "Say what you are checking (at least 10 characters). Don't write personal data.",
    duration: "Duration",
    durationOption: "{count} min",
    password: "Your password",
    passwordHint: "Enter your password again for security.",
    info: "Read-only: nothing can be changed; file contents, payslips and exports don't open; national ID, IBAN and personal contact details stay hidden. The session is written to the company's audit log and the company's HR managers and system admins are notified.",
    submit: "Start support session",
    activeExists: "You have an open support session ({company}). End it before starting a new one.",
    endActive: "End open session",
  },
  statusDialog: {
    suspendTitle: "Suspend company",
    reactivateTitle: "Reopen company",
    suspendConsequence: "All {accounts} users of {company} are signed out at once and nobody can sign in. Notifications and emails stop, invitations can't be accepted. No data is deleted; reopening continues where it left off.",
    reactivateConsequence: "Users can sign in again. Leave accruals missed meanwhile are written by the first daily job.",
    reason: "Reason",
    reasonHint: "At least 10 characters. Also written to the company's audit log.",
    confirm: "Type the company name to confirm",
    confirmMismatch: "Company name doesn't match.",
    password: "Your password",
    suspendSubmit: "Suspend company",
    reactivateSubmit: "Reopen company",
  },
  people: {
    title: "Find a person",
    lead: "Account lookup across companies. Only an exact email address or mobile number finds a person; no name or partial search.",
    search: "Email or mobile number",
    searchPlaceholder: "name@company.com or 0532 123 45 67",
    submit: "Search",
    tooShort: "Type at least 3 characters.",
    invalid: "Enter a valid email address or mobile number.",
    empty: "No account with this detail. The invitation may not be accepted yet; check the company's HR screens.",
    logged: "Searches are written to the platform log, masked.",
    lastLogin: "Last sign-in",
    locked: "Account temporarily locked",
    memberships: "Companies",
    membershipActive: "Active membership",
    membershipDisabled: "Closed membership",
    viewAs: "View as company",
    openTenant: "Open company",
    resetHint: "The company's HR manager can create a password reset code; the panel never changes passwords or emails.",
  },
  jobs: {
    title: "Background jobs",
    lead: "Email, notification and daily jobs. Job payloads are never shown; emails and phone numbers in errors are masked.",
    tabs: "Job status",
    statuses: { dead: "Failed", pending: "Pending", running: "Running", skipped: "Skipped", done: "Finished" },
    kinds: {
      notification_deliver: "Notification delivery",
      email_invitation: "Invitation email",
      email_password_reset: "Password reset email",
      leave_accrue_tenant: "Leave accrual (daily)",
      maintenance_daily: "Daily maintenance",
    },
    caption: "Background jobs",
    colKind: "Type",
    colTenant: "Company",
    colStatus: "Status",
    colAttempts: "Attempts",
    colTime: "Time",
    colError: "Last error",
    platformJob: "Platform job",
    retry: "Retry",
    retried: "The job was queued again.",
    empty: "No jobs in this state.",
  },
  audit: {
    title: "Platform log",
    lead: "Everything done in the panel: sign-ins, support sessions, suspensions, person searches. Entries can't be changed.",
    filter: "Action",
    filterAll: "All (excluding support requests)",
    filterLogins: "Sign-ins",
    filterSupport: "Support sessions",
    filterRequests: "Requests in support sessions",
    filterTenant: "Company status",
    filterSearch: "Person searches",
    caption: "Platform log",
    colAt: "Time",
    colAdmin: "Admin",
    colAction: "Action",
    colTenant: "Company",
    colDetail: "Detail",
    colIp: "IP",
    empty: "No entries.",
    meta: {
      reason: "Reason: {value}",
      duration: "{value} min",
      target: "as {value}",
      endReason: "End: {value}",
      term: "Searched: {value} ({count} results)",
      request: "{method} {route} → {status}",
      secondFactor: "With two-step verification",
    },
    failReasons: {
      unknown: "Unknown account",
      password: "Wrong password",
      disabled: "Account disabled",
      locked: "Account locked",
      totp_invalid: "Wrong verification code",
      totp_reused: "Verification code reused",
      totp_not_enrolled: "Two-step verification not on (required)",
    },
  },
  admins: {
    title: "Admins",
    lead: "Panel accounts. There is one role in this version; adding and disabling is done from the command line.",
    cli: "To add an admin: pnpm --filter @hik/api platform-admin create --email … --name …",
    caption: "Platform admins",
    colName: "Name",
    colEmail: "Email",
    colStatus: "Status",
    colLastLogin: "Last sign-in",
    colSupport: "Open support session",
    colTwoFactor: "Two-step verification",
    twoFactor:
      "Two-step verification (TOTP) is turned on from the command line: platform-admin totp-enable --email … It is required by default in production; admins without it can't sign in.",
  },
  activeSupport: {
    title: "You have an open support session",
    body: "{company} · as {name} · {left}",
    goApp: "Back to company screens",
    end: "End session",
  },
  banner: {
    label: "Support session",
    title: `${BRAND.name} support session`,
    viewingAs: "Viewing as {name}",
    readOnly: "Read-only",
    end: "End session",
    short: "Support · {name}",
    tabPrefix: "Support",
  },
  ended: {
    title: "Support session ended",
    lead: "The company session in this browser has closed.",
    reasons: {
      support_ended: "The session was ended or expired.",
      support_access_disabled: `The company turned off ${BRAND.name} support access.`,
      tenant_suspended: "The company was suspended.",
      session_expired: "The session expired.",
    },
    back: "Back to the admin panel",
  },
  supportAccess: {
    title: `${BRAND.name} support access`,
    sectionDescription: `Whether the ${BRAND.name} support team can view your account read-only; past support sessions.`,
    lead: `The ${BRAND.name} support team can open a read-only support session in your account to look into problems. Every session is recorded.`,
    status: "Status",
    on: "On",
    off: "Off",
    changed: "{date} · {name}",
    rules: {
      readOnly: "Nothing can be changed in a support session; payslip, document and file contents can't be opened and nothing can be exported.",
      masked: "National ID, IBAN and personal contact details stay hidden.",
      limit: "Each session lasts at most 60 minutes and needs a reason; who looked, when and as whom is shown below and in the audit log.",
      notify: "HR managers and system admins get an in-app notification when a session starts.",
    },
    turnOff: "Turn off support access",
    turnOn: "Turn on support access",
    confirmOffTitle: "Turn off support access?",
    confirmOff: `Open support sessions end immediately. The ${BRAND.name} support team can't see your account until you turn it back on.`,
    confirmOffSubmit: "Turn off access",
    noChange: "You can't change this setting; only a system admin can.",
    sessions: "Support sessions",
    sessionsEmpty: "No support session has been opened yet.",
    caption: "Support sessions",
    colWhen: "Date",
    colAdmin: `${BRAND.name} staff`,
    colTarget: "Viewed as",
    colReason: "Reason",
    colDuration: "Duration",
    colEnd: "End",
    auditLink: "See in the audit log",
  },
  suspendedLogin: `Your company's ${BRAND.name} account is suspended. Contact your company administrator.`,
};
